import React, { useEffect, useState, useMemo } from 'react';
import { Card } from 'reactstrap';
import { Pie } from 'react-chartjs-2';
import {
 Chart as ChartJS,
 ArcElement,
 Tooltip,
 Legend
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const PromotionsMNOsStatistics = ({ data }) => {
 const [pageData, setPageData] = useState(data);

 useEffect(() => {
  setPageData(data);
 }, [data]);

 const grouped = pageData?.grouped_mnos || [];

 // ✅ Normalize labels
 const labels = useMemo(
  () => grouped.map(item => (item.name || 'unknown').toUpperCase()),
  [grouped]
 );

 const values = useMemo(
  () => grouped.map(item => item.count),
  [grouped]
 );

 // ✅ Softer + slightly transparent palette
 const COLOR_MAP = {
  MTN: 'rgba(230, 184, 0, 0.75)',     // softer yellow
  AIRTEL: 'rgba(197, 48, 62, 0.75)',  // muted red
  ZAMTEL: 'rgba(58, 127, 120, 0.75)', // toned teal
  UNKNOWN: 'rgba(138, 143, 152, 0.75)' // soft gray
 };

 const backgroundColors = useMemo(
  () => labels.map(label => COLOR_MAP[label] || COLOR_MAP.UNKNOWN),
  [labels]
 );

 const chartData = {
  labels,
  datasets: [
   {
    data: values,
    backgroundColor: backgroundColors,
    borderWidth: 2,
    borderColor: '#f1f1f1', // softer than pure white
    hoverOffset: 10
   }
  ]
 };

 const total = useMemo(
  () => values.reduce((sum, val) => sum + val, 0),
  [values]
 );

 const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
   legend: {
    position: 'bottom',
    labels: {
     usePointStyle: true,
     pointStyle: 'circle',
     padding: 12,
     generateLabels: (chart) => {
      const data = chart.data;
      return data.labels.map((label, i) => ({
       text: label,
       fillStyle: data.datasets[0].backgroundColor[i],
       strokeStyle: data.datasets[0].backgroundColor[i],
       index: i
      }));
     }
    }
   },
   tooltip: {
    callbacks: {
     label: function (context) {
      const label = context.label || '';
      const value = context.raw || 0;
      const percentage =
       total > 0 ? ((value / total) * 100).toFixed(1) : 0;

      return `${label}: ${value.toLocaleString()} (${percentage}%)`;
     }
    }
   }
  }
 };

 return (
  <Card className="h-100 shadow-sm">
   <div className="card-inner p-3">

    {/* Header */}
    <div className="card-title-group mb-3">
     <div className="card-title">
      <h6 className="title mb-1">MNO Distribution</h6>
      <small className="text-muted">
       Total Subscribers: {total.toLocaleString()}
      </small>
     </div>
    </div>

    {/* Chart */}
    <div style={{ height: '260px', position: 'relative' }}>
     {values.length > 0 ? (
      <Pie data={chartData} options={options} />
     ) : (
      <div className="d-flex align-items-center justify-content-center h-100">
       <p className="text-muted mb-0">No data available</p>
      </div>
     )}
    </div>

    {/* Badges */}
    {values.length > 0 && (
     <div
      className="mt-3 d-flex flex-wrap justify-content-center"
      style={{ gap: '6px' }}
     >
      {labels.map((label, idx) => (
       <span
        key={idx}
        className="badge"
        style={{
         backgroundColor: backgroundColors[idx],
         color: '#fff',
         fontSize: '11px',
         padding: '6px 10px',
         borderRadius: '6px',
         opacity: 0.9 // 👈 slightly softened for UI
        }}
       >
        {label}: {values[idx].toLocaleString()}
       </span>
      ))}
     </div>
    )}
   </div>
  </Card>
 );
};

export default PromotionsMNOsStatistics;